import { categoryStructure } from "../categoryStructure";
import {
  getFamilyRuntime,
  readFamilySpec,
  type ListingFamilySpecs,
} from "./listingFamilyRuntime";
import {
  composeSimpleListingTitle,
  resolveListingTitlePatternKind,
} from "./listingTitlePatterns";

/**
 * Composes a title from brand, size, model, and subcategory
 * Format: [Size] [Brand] [Model] [Subcategory Name]
 *
 * Examples (size uses abbreviated units from the sell form when applicable):
 * - "55 In LG OLED55C1PUB Smart TV"
 * - "200L Haier Thermocool Standing Fridge"
 * - "9KVA Firman Generator"
 * - "2HP LG Dual Inverter AC"
 */
export interface TitleComponents {
  size?: string;
  brand?: string;
  model?: string;
  subcategoryId?: string;
  subcategoryName?: string;
  manualOverride?: string;
  familySpecs?: ListingFamilySpecs;
}

/**
 * Compose title from components.
 *
 * Whole-sell entry: rich listing-family adapters when present; otherwise the
 * default simple map through {@link composeSimpleListingTitle} (D8 / D13).
 */
export function composeTitle(components: TitleComponents): string {
  if (components.manualOverride) {
    return components.manualOverride;
  }

  const familyRuntime = getFamilyRuntime(components.subcategoryId);
  const patternKind = resolveListingTitlePatternKind(components.subcategoryId, {
    hasRichAdapter: Boolean(familyRuntime),
  });

  if (patternKind === "rich" && familyRuntime) {
    const attrs = familyRuntime.buildAttrs(
      readFamilySpec(components.familySpecs, familyRuntime.familyId),
      { subcategoryId: components.subcategoryId },
    );
    const familyTitle = familyRuntime.composeTitle(attrs, {
      brand: components.brand,
      model: components.model,
      subcategoryId: components.subcategoryId,
    });
    if (familyTitle) return familyTitle;
    return "";
  }

  return composeSimpleListingTitle({
    size: components.size,
    brand: components.brand,
    model: components.model,
    product: components.subcategoryName,
  });
}

/**
 * Extract components from an existing title
 * Useful for editing existing listings
 */
export function parseTitleToComponents(
  title: string,
): Partial<TitleComponents> {
  // Simple parsing - could be enhanced with AI/NLP later
  // For now, just return empty components and let user fill in fields

  // Try to extract common patterns
  const sizePattern =
    /(\d+(?:\.\d+)?\s*(?:inches?|inch|in|liters?|L|KVA|kg|ft|feet))/i;
  const sizeMatch = title.match(sizePattern);

  const components: Partial<TitleComponents> = {};

  if (sizeMatch) {
    components.size = sizeMatch[1];
  }

  return components;
}

/**
 * Get subcategory name from ID
 */
export function getSubcategoryName(
  subcategoryId: string | null | undefined,
): string | null {
  if (!subcategoryId) return null;

  for (const category of categoryStructure) {
    const subcategory = category.subcategories.find(
      (sub) => sub.id === subcategoryId,
    );
    if (subcategory) {
      return subcategory.name;
    }
  }

  return null;
}

/**
 * Generate title suggestions based on components
 */
export function generateTitleSuggestions(
  components: TitleComponents,
): string[] {
  const suggestions: string[] = [];

  // Main suggestion
  const mainTitle = composeTitle(components);
  if (mainTitle) {
    suggestions.push(mainTitle);
  }

  // Alternative format: Brand + Model + Size + Subcategory
  if (
    components.brand &&
    components.model &&
    components.size &&
    components.subcategoryName
  ) {
    suggestions.push(
      `${components.brand} ${components.model} ${components.size} ${components.subcategoryName}`,
    );
  }

  // Alternative: Size + Subcategory + Brand (if no model)
  if (
    components.size &&
    components.subcategoryName &&
    components.brand &&
    !components.model
  ) {
    suggestions.push(
      `${components.size} ${components.subcategoryName} - ${components.brand}`,
    );
  }

  return suggestions.filter((title) => title.trim().length > 0);
}
